import { useRef, useState } from 'react'
import type { AlertSummary } from '../../types/cap'
import { useAlertStore } from '../../store/alertStore'
import { useNow } from '../../hooks/useNow'
import { HazardBadge, SeverityBadge, PriorityDot } from './AlertBadge'

interface AlertCardProps {
  alert: AlertSummary
}

function formatRelativeTime(isoString: string | null, now: number): string {
  if (!isoString) return ''
  const diff = now - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function formatExpiry(isoString: string | null, now: number): string | null {
  if (!isoString) return null
  const diff = new Date(isoString).getTime() - now
  if (diff <= 0) return 'Expired'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m left`
  const hrs = Math.floor(diff / 3_600_000)
  if (hrs < 24) return `${hrs}h left`
  return `${Math.floor(hrs / 24)}d left`
}

const DISMISS_THRESHOLD = 80

export default function AlertCard({ alert }: AlertCardProps) {
  const { selectedAlertId, selectAlert, dismissAlert } = useAlertStore()
  const now = useNow()
  const isSelected = selectedAlertId === alert.id
  const expiry = formatExpiry(alert.expires_at, now)

  const [swipeX, setSwipeX] = useState(0)
  const [animating, setAnimating] = useState(false)
  const swipeXRef = useRef(0)
  const startXRef = useRef<number | null>(null)
  const didSwipeRef = useRef(false)

  const applySwipeX = (v: number) => {
    swipeXRef.current = v
    setSwipeX(v)
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    startXRef.current = e.clientX
    didSwipeRef.current = false
    setAnimating(false)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startXRef.current === null) return
    const dx = e.clientX - startXRef.current
    if (dx < -4) {
      didSwipeRef.current = true
      applySwipeX(Math.max(dx, -160))
    }
  }

  const handlePointerUp = () => {
    if (startXRef.current === null) return
    startXRef.current = null
    setAnimating(true)
    if (swipeXRef.current < -DISMISS_THRESHOLD) {
      applySwipeX(-400)
      setTimeout(() => dismissAlert(alert.id), 200)
    } else {
      applySwipeX(0)
    }
  }

  const swipeProgress = Math.min(Math.abs(swipeX) / DISMISS_THRESHOLD, 1)

  return (
    <div
      className="relative rounded-lg overflow-hidden"
      style={{ touchAction: 'pan-y' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Dismiss background — revealed as card slides left */}
      <div
        className="absolute inset-0 bg-red-700 rounded-lg flex items-center justify-end pr-4 gap-1.5"
        style={{ opacity: swipeProgress }}
        aria-hidden
      >
        <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <span className="text-white text-xs font-medium">Dismiss</span>
      </div>

      {/* Card content */}
      <div
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: animating ? 'transform 200ms ease-out' : 'none',
        }}
      >
        <button
          type="button"
          onClick={() => { if (!didSwipeRef.current) selectAlert(isSelected ? null : alert.id) }}
          className={`w-full text-left p-3 rounded-lg border transition-colors ${
            isSelected
              ? 'bg-gray-200 border-gray-400 dark:bg-gray-800 dark:border-gray-600'
              : 'bg-white border-gray-200 hover:bg-gray-100 hover:border-gray-300 dark:bg-gray-900 dark:border-gray-800 dark:hover:bg-gray-800 dark:hover:border-gray-700'
          }`}
        >
          <div className="flex items-start gap-2">
            <PriorityDot score={alert.priority_score} className="mt-1.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <HazardBadge hazardType={alert.hazard_type} />
                <SeverityBadge severity={alert.severity} />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
                {alert.headline ?? 'No headline'}
              </p>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                {alert.region_name && (
                  <span className="truncate">{alert.region_name}</span>
                )}
                <span className="shrink-0">{formatRelativeTime(alert.sent_at, now)}</span>
                {expiry && (
                  <span className={`shrink-0 ${
                    expiry === 'Expired' ? 'text-red-600 dark:text-red-500' :
                    expiry.endsWith('m left') ? 'text-amber-600 dark:text-amber-400' : ''
                  }`}>
                    · {expiry}
                  </span>
                )}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  )
}
